import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../Config/config';
import {
 LockClosedIcon,
 EnvelopeIcon,
 CheckCircleIcon,
 ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const LockedAccount = () => {
 const navigate = useNavigate();
 const location = useLocation();
 const [userId, setUserId] = useState('');
 const [message, setMessage] = useState('');
 const [loading, setLoading] = useState(false);
 const [success, setSuccess] = useState(false);
 const [error, setError] = useState('');

 // Get userId from location state or localStorage
 useEffect(() => {
 const stateUserId = location.state?.userId;
 const storedUserId = localStorage.getItem('user_id');
 setUserId(stateUserId || storedUserId || '');
 }, [location]);

 const handleSubmit = async (e) => {
 e.preventDefault();
 
 if (!userId) {
 setError('User ID is required. Please enter your username or email.');
 return;
 }

 setLoading(true);
 setError('');
 setSuccess(false);

 try {
 const response = await axios.post(`${API_BASE_URL}/user/request-unlock`, {
 userId: userId.trim(),
 message: message.trim() || undefined
 });

 if (response.data && response.data.message) {
 setSuccess(true);
 setError('');
 // Clear form after 3 seconds
 setTimeout(() => {
 setMessage('');
 }, 3000);
 }
 } catch (err) {
 const errorMsg = err.response?.data?.error || 
 err.response?.data?.message || 
 err.message || 
 'Failed to send unlock request. Please try again.';
 setError(errorMsg);
 setSuccess(false);
 } finally {
 setLoading(false);
 }
 };

 return (
 <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
 <div className="max-w-md w-full bg-bgSection rounded-lg shadow-xl p-8 border border-gray-700">
 {/* Header */}
 <div className="text-center mb-6">
 <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-500/20 mb-4">
 <LockClosedIcon className="h-8 w-8 text-red-400" />
 </div>
 <h1 className="text-2xl font-bold text-white mb-2">Account Locked</h1>
 <p className="text-muted text-sm">
 Your account has been temporarily locked due to multiple failed login attempts.
 </p>
 </div>

 {/* Success Message */}
 {success && (
 <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg flex items-start gap-3">
 <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
 <div className="flex-1">
 <p className="text-green-400 font-semibold">Request Sent Successfully!</p>
 <p className="text-green-300 text-sm mt-1">
 Your unlock request has been sent to the administrator. You will receive an email confirmation shortly.
 </p>
 </div>
 </div>
 )}

 {/* Error Message */}
 {error && (
 <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-start gap-3">
 <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
 <div className="flex-1">
 <p className="text-red-400 font-semibold">Error</p>
 <p className="text-red-300 text-sm mt-1">{error}</p>
 </div>
 </div>
 )}

 {/* Form */}
 {!success && (
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <label htmlFor="userId" className="block text-sm font-medium text-muted mb-2">
 Username or Email
 </label>
 <input
 type="text"
 id="userId"
 value={userId}
 onChange={(e) => setUserId(e.target.value)}
 className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
 placeholder="Enter your username or email"
 required
 disabled={loading}
 />
 </div>

 <div>
 <label htmlFor="message" className="block text-sm font-medium text-muted mb-2">
 Additional Message (Optional)
 </label>
 <textarea
 id="message"
 value={message}
 onChange={(e) => setMessage(e.target.value)}
 rows={4}
 className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
 placeholder="Please provide any additional information that might help..."
 disabled={loading}
 />
 </div>

 <button
 type="submit"
 disabled={loading || !userId.trim()}
 className="w-full bg-secondary hover:bg-primary disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-semibold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2"
 >
 {loading ? (
 <>
 <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
 </svg>
 Sending Request...
 </>
 ) : (
 <>
 <EnvelopeIcon className="h-5 w-5" />
 Send Unlock Request
 </>
 )}
 </button>
 </form>
 )}

 {/* Info Box */}
 <div className="mt-6 p-4 bg-blue-500/20 border border-blue-500/50 rounded-lg">
 <p className="text-blue-300 text-sm">
 <strong className="text-blue-400">What happens next?</strong>
 <br />
 Your request will be sent to the administrator. They will review your request and unlock your account. You will receive an email notification once your account has been unlocked.
 </p>
 </div>

 {/* Back to Login */}
 <div className="mt-6 text-center">
 <button
 onClick={() => navigate('/login')}
 className="text-muted hover:text-white text-sm transition-colors"
 >
 ← Back to Login
 </button>
 </div>
 </div>
 </div>
 );
};

export default LockedAccount;








